import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createAdminUser,
  fetchAdminDepartments,
  fetchAdminRoles,
  fetchAdminUsers,
  updateAdminUser,
} from '../../services/adminUsersService.js';

const AGENT_QUEUE_OPTIONS = ['urdu-q', 'english-q', 'sindhi-q', 'punjabi-q'];
const EMAIL_MAX_LENGTH = 50;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ERROR_ALIASES = {
  LoginName: 'loginName',
  loginname: 'loginName',
  FullName: 'fullName',
  fullname: 'fullName',
  DepartmentId: 'departmentId',
  departmentid: 'departmentId',
  RoleIds: 'roleIds',
  roleids: 'roleIds',
  'extension.extensionnumber': 'extension.extensionNumber',
  'extension.ExtensionNumber': 'extension.extensionNumber',
  'extension.queuename': 'extension.queueName',
  'extension.QueueName': 'extension.queueName',
  emailaddress: 'emailAddress',
  email_address: 'emailAddress',
  EmailAddress: 'emailAddress',
};

const normalizeQueueNames = (value) => {
  const values = Array.isArray(value) ? value : String(value || '').split(',');
  return Array.from(new Set(
    values
      .map((queueName) => String(queueName || '').trim())
      .filter((queueName) => AGENT_QUEUE_OPTIONS.includes(queueName))
  ));
};

const formatQueueNames = (value) => normalizeQueueNames(value).join(',');

const emptyForm = {
  userId: null,
  loginName: '',
  password: '',
  fullName: '',
  emailAddress: '',
  phoneNo: '',
  mobileNo: '',
  departmentId: '',
  roleIds: [],
  isActive: true,
  extension: {
    extensionNumber: '',
    sipUsername: '',
    sipPassword: '',
    queueName: '',
    isEnabled: true,
  },
};

const normalizeRoleIds = (value) => (Array.isArray(value) ? value.map(Number).filter(Boolean) : []);

const isValidOptionalEmail = (value) => {
  const email = String(value || '').trim();
  return !email || EMAIL_PATTERN.test(email);
};

const normalizeValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return {};
  return Object.entries(errors).reduce((acc, [field, value]) => {
    const normalizedField = FIELD_ERROR_ALIASES[field] || field;
    const message = Array.isArray(value) ? value.filter(Boolean).join(' ') : String(value || '');
    if (message) acc[normalizedField] = message;
    return acc;
  }, {});
};

const firstValidationMessage = (errors) => Object.values(errors).find(Boolean) || '';

const requiredMark = <span className="text-danger ms-1" aria-hidden="true">*</span>;

const buildClientValidationErrors = (form = {}, isEdit = false) => {
  const errors = {};
  const extension = form.extension || {};
  const email = String(form.emailAddress || '').trim();

  if (!String(form.loginName || '').trim()) errors.loginName = 'Login Name is required.';
  if (!isEdit && !String(form.password || '').trim()) errors.password = 'Password is required.';
  if (form.password && String(form.password).length < 6) errors.password = 'Password must be at least 6 characters.';
  if (!String(form.fullName || '').trim()) errors.fullName = 'Full Name is required.';
  if (email.length > EMAIL_MAX_LENGTH) {
    errors.emailAddress = `Email must be ${EMAIL_MAX_LENGTH} characters or fewer.`;
  } else if (!isValidOptionalEmail(email)) {
    errors.emailAddress = 'Enter a valid Email address.';
  }
  if (!form.departmentId) errors.departmentId = 'Department is required.';
  if (!Array.isArray(form.roleIds) || !form.roleIds.length) errors.roleIds = 'At least one role is required.';

  const hasExtension =
    String(extension.extensionNumber || '').trim() ||
    String(extension.sipUsername || '').trim() ||
    String(extension.sipPassword || '').trim() ||
    String(extension.queueName || '').trim();

  if (hasExtension && !String(extension.extensionNumber || '').trim()) {
    errors['extension.extensionNumber'] = 'Extension Number is required when SIP fields are filled.';
  }
  if (extension.queueName && normalizeQueueNames(extension.queueName).join(',') !== extension.queueName) {
    errors['extension.queueName'] = 'Select a valid queue.';
  }

  return errors;
};

const describeRequestFailure = (reason) => {
  if (reason?.code === 'ECONNABORTED') return 'request timed out';
  if (reason?.response?.status) return `HTTP ${reason.response.status}`;
  return reason?.response?.data?.message || reason?.response?.data?.error || reason?.message || 'request failed';
};

const failureLabel = (label, result) => (
  result.status === 'rejected' ? `${label} (${describeRequestFailure(result.reason)})` : ''
);

const roleLabel = (role = {}) => {
  const code = role.shortCode || role.ShortCode || '';
  const name = role.roleName || role.RoleName || '';
  return code ? `${name} (${code})` : name;
};

const formatRoles = (roles = []) => {
  if (!Array.isArray(roles) || roles.length === 0) return '-';
  return roles.map((role) => role.shortCode || role.ShortCode || role.roleName || role.RoleName).filter(Boolean).join(', ');
};

const toForm = (user = {}) => ({
  userId: user.userId || null,
  loginName: user.loginName || '',
  password: '',
  fullName: user.fullName || '',
  emailAddress: user.emailAddress || '',
  phoneNo: user.phoneNo || '',
  mobileNo: user.mobileNo || '',
  departmentId: user.departmentId ? String(user.departmentId) : '',
  roleIds: normalizeRoleIds(user.roleIds),
  isActive: Boolean(user.isActive),
  extension: {
    extensionNumber: user.extension?.extensionNumber || '',
    sipUsername: user.extension?.sipUsername || '',
    sipPassword: '',
    queueName: formatQueueNames(user.extension?.queueName || ''),
    isEnabled: user.extension?.isEnabled !== undefined ? Boolean(user.extension.isEnabled) : true,
  },
});

function UserForm({
  form,
  departments,
  roles,
  isEdit,
  saving,
  error,
  fieldErrors = {},
  onChange,
  onExtensionChange,
  onRoleToggle,
  onSubmit,
  onCancel,
}) {
  const selectedQueues = normalizeQueueNames(form.extension.queueName);
  const loginNameError = fieldErrors.loginName || '';
  const passwordError = fieldErrors.password || '';
  const fullNameError = fieldErrors.fullName || '';
  const emailError = fieldErrors.emailAddress || '';
  const departmentError = fieldErrors.departmentId || '';
  const roleIdsError = fieldErrors.roleIds || '';
  const extensionNumberError = fieldErrors['extension.extensionNumber'] || '';
  const queueNameError = fieldErrors['extension.queueName'] || '';
  const toggleQueue = (queueName) => {
    const nextQueues = selectedQueues.includes(queueName)
      ? selectedQueues.filter((selectedQueue) => selectedQueue !== queueName)
      : [...selectedQueues, queueName];
    onExtensionChange('queueName', nextQueues.join(','));
  };

  return (
    <form className="border rounded bg-white p-3 shadow-sm" onSubmit={onSubmit} noValidate>
      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h5 className="mb-1">{isEdit ? 'Edit User' : 'Add User'}</h5>
          <small className="text-muted">Department and at least one role are required for login.</small>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
          <i className="bi bi-x-lg me-1"></i>
          Close
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Login Name{requiredMark}</label>
          <input
            className={`form-control ${loginNameError ? 'is-invalid' : ''}`}
            value={form.loginName}
            onChange={(e) => onChange('loginName', e.target.value)}
            maxLength={30}
            aria-invalid={Boolean(loginNameError)}
            aria-describedby={loginNameError ? 'admin-user-login-error' : undefined}
          />
          {loginNameError && (
            <div id="admin-user-login-error" className="invalid-feedback d-block">{loginNameError}</div>
          )}
        </div>
        <div className="col-md-4">
          <label className="form-label">
            Password{!isEdit && requiredMark}
          </label>
          <input
            className={`form-control ${passwordError ? 'is-invalid' : ''}`}
            type="password"
            value={form.password}
            onChange={(e) => onChange('password', e.target.value)}
            placeholder={isEdit ? 'Leave blank to keep current password' : ''}
            minLength={isEdit && !form.password ? undefined : 6}
            aria-invalid={Boolean(passwordError)}
            aria-describedby={passwordError ? 'admin-user-password-error' : undefined}
          />
          {passwordError && (
            <div id="admin-user-password-error" className="invalid-feedback d-block">{passwordError}</div>
          )}
        </div>
        <div className="col-md-4">
          <label className="form-label">Full Name{requiredMark}</label>
          <input
            className={`form-control ${fullNameError ? 'is-invalid' : ''}`}
            value={form.fullName}
            onChange={(e) => onChange('fullName', e.target.value)}
            maxLength={50}
            aria-invalid={Boolean(fullNameError)}
            aria-describedby={fullNameError ? 'admin-user-fullname-error' : undefined}
          />
          {fullNameError && (
            <div id="admin-user-fullname-error" className="invalid-feedback d-block">{fullNameError}</div>
          )}
        </div>

        <div className="col-md-4">
          <label className="form-label">Email</label>
          <input
            className={`form-control ${emailError ? 'is-invalid' : ''}`}
            type="email"
            value={form.emailAddress}
            onChange={(e) => onChange('emailAddress', e.target.value)}
            maxLength={EMAIL_MAX_LENGTH}
            autoComplete="email"
            title="Enter a valid email address, e.g. user@example.com"
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'admin-user-email-error' : 'admin-user-email-help'}
          />
          {emailError ? (
            <div id="admin-user-email-error" className="invalid-feedback d-block">{emailError}</div>
          ) : (
            <div id="admin-user-email-help" className="form-text">Max {EMAIL_MAX_LENGTH} characters.</div>
          )}
        </div>
        <div className="col-md-4">
          <label className="form-label">Phone</label>
          <input
            className="form-control"
            value={form.phoneNo}
            onChange={(e) => onChange('phoneNo', e.target.value)}
            maxLength={15}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Mobile</label>
          <input
            className="form-control"
            value={form.mobileNo}
            onChange={(e) => onChange('mobileNo', e.target.value)}
            maxLength={15}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Department{requiredMark}</label>
          <select
            className={`form-select ${departmentError ? 'is-invalid' : ''}`}
            value={form.departmentId}
            onChange={(e) => onChange('departmentId', e.target.value)}
            aria-invalid={Boolean(departmentError)}
            aria-describedby={departmentError ? 'admin-user-department-error' : undefined}
          >
            <option value="">Select department</option>
            {departments.map((department) => (
              <option key={department.departmentId} value={department.departmentId}>
                {department.departmentName}
              </option>
            ))}
          </select>
          {departmentError && (
            <div id="admin-user-department-error" className="invalid-feedback d-block">{departmentError}</div>
          )}
        </div>
        <div className="col-md-8">
          <label className="form-label">Roles{requiredMark}</label>
          <div
            className={`d-flex flex-wrap gap-2 border rounded p-2 bg-light ${roleIdsError ? 'border-danger' : ''}`}
            aria-invalid={Boolean(roleIdsError)}
            aria-describedby={roleIdsError ? 'admin-user-roles-error' : undefined}
          >
            {roles.map((role) => {
              const id = Number(role.roleId);
              const checked = form.roleIds.includes(id);
              return (
                <label key={id} className="form-check form-check-inline mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={checked}
                    onChange={() => onRoleToggle(id)}
                  />
                  <span className="form-check-label">{roleLabel(role)}</span>
                </label>
              );
            })}
            {roles.length === 0 && <span className="text-muted small">No roles loaded.</span>}
          </div>
          {roleIdsError && (
            <div id="admin-user-roles-error" className="invalid-feedback d-block">{roleIdsError}</div>
          )}
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              id="admin-user-active"
              className="form-check-input"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => onChange('isActive', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="admin-user-active">Active</label>
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Extension / SIP Configuration</strong>
              <div className="form-check form-switch mb-0">
                <input
                  id="extension-enabled"
                  className="form-check-input"
                  type="checkbox"
                  checked={form.extension.isEnabled}
                  onChange={(e) => onExtensionChange('isEnabled', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="extension-enabled">Enabled</label>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Extension Number</label>
                <input
                  className={`form-control ${extensionNumberError ? 'is-invalid' : ''}`}
                  value={form.extension.extensionNumber}
                  onChange={(e) => onExtensionChange('extensionNumber', e.target.value)}
                  maxLength={20}
                  aria-invalid={Boolean(extensionNumberError)}
                  aria-describedby={extensionNumberError ? 'admin-user-extension-error' : undefined}
                />
                {extensionNumberError && (
                  <div id="admin-user-extension-error" className="invalid-feedback d-block">{extensionNumberError}</div>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">SIP Username</label>
                <input
                  className="form-control"
                  value={form.extension.sipUsername}
                  onChange={(e) => onExtensionChange('sipUsername', e.target.value)}
                  maxLength={50}
                  placeholder="Defaults to extension"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">SIP Password</label>
                <input
                  className="form-control"
                  type="password"
                  value={form.extension.sipPassword}
                  onChange={(e) => onExtensionChange('sipPassword', e.target.value)}
                  maxLength={100}
                  placeholder={isEdit ? 'Leave blank to keep current SIP password' : ''}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Queues</label>
                <div
                  className={`border rounded bg-white px-2 py-1 ${queueNameError ? 'border-danger' : ''}`}
                  style={{ minHeight: 38 }}
                  aria-invalid={Boolean(queueNameError)}
                  aria-describedby={queueNameError ? 'admin-user-queue-error' : undefined}
                >
                  <div className="d-flex flex-wrap gap-2">
                    {AGENT_QUEUE_OPTIONS.map((queueName) => (
                      <label key={queueName} className="form-check form-check-inline mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedQueues.includes(queueName)}
                          onChange={() => toggleQueue(queueName)}
                        />
                        <span className="form-check-label">{queueName}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {queueNameError && (
                  <div id="admin-user-queue-error" className="invalid-feedback d-block">{queueNameError}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-save'} me-1`}></i>
          {saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}

export default function UserManager() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  const isEdit = Boolean(form.userId);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [userResult, departmentResult, roleResult] = await Promise.allSettled([
        fetchAdminUsers(),
        fetchAdminDepartments(),
        fetchAdminRoles(),
      ]);

      if (userResult.status === 'fulfilled') setUsers(userResult.value);
      if (departmentResult.status === 'fulfilled') setDepartments(departmentResult.value);
      if (roleResult.status === 'fulfilled') setRoles(roleResult.value);

      const failures = [
        failureLabel('users', userResult),
        failureLabel('departments', departmentResult),
        failureLabel('roles', roleResult),
      ].filter(Boolean);

      if (failures.length) {
        setError(`Failed to load ${failures.join(', ')}. Please refresh.`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const openCreate = useCallback(() => {
    setForm(emptyForm);
    setFormError('');
    setFieldErrors({});
    setShowForm(true);
  }, []);

  const openEdit = useCallback((user) => {
    setForm(toForm(user));
    setFormError('');
    setFieldErrors({});
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setForm(emptyForm);
    setFormError('');
    setFieldErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateExtension = useCallback((field, value) => {
    setForm((prev) => ({
      ...prev,
      extension: {
        ...prev.extension,
        [field]: value,
      },
    }));
    const errorKey = `extension.${field}`;
    setFieldErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  }, []);

  const toggleRole = useCallback((roleId) => {
    setForm((prev) => {
      const existing = normalizeRoleIds(prev.roleIds);
      return {
        ...prev,
        roleIds: existing.includes(roleId)
          ? existing.filter((id) => id !== roleId)
          : [...existing, roleId],
      };
    });
    setFieldErrors((prev) => {
      if (!prev.roleIds) return prev;
      const next = { ...prev };
      delete next.roleIds;
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    return buildClientValidationErrors(form, isEdit);
  }, [form, isEdit]);

  const submitForm = useCallback(async (event) => {
    event.preventDefault();
    setFormError('');
    setFieldErrors({});
    const validationErrors = validateForm();
    const validationMessage = firstValidationMessage(validationErrors);
    if (validationMessage) {
      setFormError('Please complete the highlighted required fields.');
      setFieldErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateAdminUser(form.userId, form);
        setSuccess('User updated.');
      } else {
        await createAdminUser(form);
        setSuccess('User created.');
      }
      closeForm();
      await loadAll();
    } catch (err) {
      const validationErrors = normalizeValidationErrors(err?.response?.data?.errors);
      const firstMessage = firstValidationMessage(validationErrors);
      setFieldErrors(validationErrors);
      setFormError(
        firstMessage ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          (err?.response?.status === 422 ? 'Validation failed. Please check the form fields.' : '') ||
          err?.message ||
          'Save failed.'
      );
    } finally {
      setSaving(false);
    }
  }, [closeForm, form, isEdit, loadAll, validateForm]);

  const toggleActive = useCallback(async (user) => {
    const nextActive = !user.isActive;
    const verb = nextActive ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${verb} ${user.fullName || user.loginName}?`)) return;

    setSaving(true);
    setError('');
    try {
      await updateAdminUser(user.userId, {
        ...toForm(user),
        isActive: nextActive,
      });
      setSuccess(nextActive ? 'User activated.' : 'User deactivated.');
      await loadAll();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Status update failed.');
    } finally {
      setSaving(false);
    }
  }, [loadAll]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => [
      user.loginName,
      user.fullName,
      user.departmentName,
      formatRoles(user.roles),
      user.extension?.extensionNumber,
      user.extension?.queueName,
    ].some((value) => String(value || '').toLowerCase().includes(q)));
  }, [search, users]);

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
        <div>
          <h4 className="mb-1">Admin User Management</h4>
          <small className="text-muted">Manage CRM users, roles, departments, and SIP extensions.</small>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-danger" onClick={() => navigate('/logout')}>
            <i className="bi bi-box-arrow-right me-1"></i>
            Logout
          </button>
          <button className="btn btn-outline-primary" onClick={() => navigate('/admin/generic')}>
            <i className="bi bi-list-ul me-1"></i>
            Generic
          </button>
          <button className="btn btn-outline-primary" onClick={() => navigate('/admin/music-on-hold')}>
            <i className="bi bi-music-note-beamed me-1"></i>
            Music on Hold
          </button>
          <button className="btn btn-outline-secondary" onClick={loadAll} disabled={loading || saving}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            <i className="bi bi-person-plus me-1"></i>
            Add User
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {showForm && (
        <div className="mb-3">
          <UserForm
            form={form}
            departments={departments}
            roles={roles}
            isEdit={isEdit}
            saving={saving}
            error={formError}
            fieldErrors={fieldErrors}
            onChange={updateField}
            onExtensionChange={updateExtension}
            onRoleToggle={toggleRole}
            onSubmit={submitForm}
            onCancel={closeForm}
          />
        </div>
      )}

      <div className="border rounded bg-white shadow-sm">
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap p-3 border-bottom">
          <strong>Users</strong>
          <div className="input-group" style={{ maxWidth: 360 }}>
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-muted">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-4 text-center text-muted">No users found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Login Name</th>
                  <th>Full Name</th>
                  <th>Department</th>
                  <th>Roles</th>
                  <th>Extension</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.loginName}</td>
                    <td>{user.fullName}</td>
                    <td>{user.departmentName || '-'}</td>
                    <td>{formatRoles(user.roles)}</td>
                    <td>
                      {user.extension?.extensionNumber ? (
                        <div>
                          <div>
                            {user.extension.extensionNumber}
                            {!user.extension.isEnabled && <small className="text-muted ms-1">(disabled)</small>}
                          </div>
                          {user.extension.queueName && (
                            <small className="text-muted">{user.extension.queueName}</small>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`badge ${user.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-primary" onClick={() => openEdit(user)}>
                          <i className="bi bi-pencil-square me-1"></i>
                          Edit
                        </button>
                        <button
                          className={`btn ${user.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                          onClick={() => toggleActive(user)}
                          disabled={saving}
                        >
                          <i className={`bi ${user.isActive ? 'bi-person-dash' : 'bi-person-check'} me-1`}></i>
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
